import { Text, Box, IconButton, Icon, useDisclosure, Tooltip, Button, Flex } from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CellValue } from 'react-table';

import { IconPen, IconTrashbin } from 'assets/icons';
import { ConfirmationModal } from 'components/Modal';
import ReactSelect from 'components/ReactSelect';
import SearchField from 'components/SearchField';
import Table from 'components/Table';
import Title from 'components/Title';
import { customPaddingStyles } from 'containers/Integrations/shared/react-select-custom';
import { routePaths } from 'global/routePaths';
import { useFetchEmployees } from 'hooks/employee/useFetchEmployees';
import { useDeleteEvvEmployee } from 'hooks/evv/useDeleteEvvEmployee';
import { IEvvEmployeesSearchQuery, useFetchPaginatedEvvEmployees } from 'hooks/evv/useFetchPaginatedEvvEmployees';
import { IMapEvvEmployeeToTimeeroUser, useMapEvvEmployeeToTimeeroUser } from 'hooks/evv/useMapEvvEmployeeToTimeeroUser';
import {
  IUnlinkEvvEmployeeToTimeeroUser,
  useUnlinkEvvEmployeeToTimeeroUser,
} from 'hooks/evv/useUnlinkEvvEmployeeToTimeeroUser';
import { formatPhoneNum } from 'utils/helpers';

const searchQueryInitials: IEvvEmployeesSearchQuery = {
  page: 1,
  page_size: 25,
  search: '',
};

interface IActiveEmployeesProps {
  initialSearchQuery?: IEvvEmployeesSearchQuery;
}

const EmployeesList: React.FC<IActiveEmployeesProps> = ({ initialSearchQuery = searchQueryInitials }) => {
  const { t } = useTranslation(['evv', 'common']);

  const [searchQuery, setSearchQuery] = useState<IEvvEmployeesSearchQuery>(initialSearchQuery);
  const [deleteId, setDeleteId] = useState<number | string | null>(null);
  const [mappedUsers, setMappedUsers] = useState<any>(null);
  const [currentMappedEmployee, setCurrentMappedEmployee] = useState<any>(null);

  const navigate = useNavigate();

  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();

  const { status, data } = useFetchPaginatedEvvEmployees(searchQuery);
  const { status: fetchEmployeesStatus, data: employeesData } = useFetchEmployees();
  const { status: mapEvvEmployeeStatus, mutateAsync: mutateAsyncMapEvvEmployee } = useMapEvvEmployeeToTimeeroUser();
  const { status: unlinkEvvEmployeeStatus, mutateAsync: mutateAsyncUnlinkEvvEmployee } =
    useUnlinkEvvEmployeeToTimeeroUser();
  const { status: deleteEmployeeStatus, mutateAsync: mutateAsyncDeleteEmployee } = useDeleteEvvEmployee();

  const columns = useMemo(
    () => [
      {
        Header: t('common:name'),
        accessor: 'first_name',
        width: 220,
        Cell: (data: CellValue) => {
          const phoneNum = data.row.original?.phone;
          let formattedPhoneNum = '-----';
          if (phoneNum) {
            formattedPhoneNum = formatPhoneNum(phoneNum);
          }

          return (
            <Box>
              <Text fontWeight={700} color="newGray.1000" mb="1">
                {`${data.row.original?.first_name} ${data.row.original?.middle_name || ''} ${
                  data.row.original?.last_name || ''
                }`}
              </Text>
              <Text fontWeight="semibold" color="gray.600">
                {data.row.original?.email}
              </Text>
              <Text fontWeight={300} color="gray.500">
                {formattedPhoneNum}
              </Text>
            </Box>
          );
        },
      },

      {
        Header: t('common:address'),
        accessor: 'full_address',
        width: 200,
      },
      {
        Header: t('common:qualifier'),
        accessor: 'qualifier',
        width: 150,
      },
      {
        Header: t('evv:timeeroUsers'),
        accessor: 'user',
        width: 220,
        Cell: (data: CellValue) => {
          return (
            <Box position="relative" width="100%">
              <ReactSelect
                options={employeesData?.payload || []}
                value={
                  data.row.original?.user_id
                    ? data.row.original?.user || null
                    : mappedUsers &&
                        mappedUsers[`${data.row.original.id}`] &&
                        mappedUsers[`${data.row.original.id}`].user
                      ? mappedUsers[`${data.row.original.id}`].user
                      : null
                }
                onChange={(value: any) => {
                  setMappedUsers((originalValues: any) => {
                    const prevValues = originalValues ? { ...originalValues } : null;

                    if (prevValues) {
                      const oldValue = Object.entries(prevValues).find(([_, prevValue]: any) => {
                        return prevValue?.user?.id === value.id;
                      });

                      if (oldValue) {
                        delete prevValues[oldValue[0]];
                      }
                    }

                    return {
                      ...prevValues,
                      [data.row.original.id]: {
                        user: value || null,
                      },
                    };
                  });
                }}
                menuPortalTarget={document.body}
                isSearchable
                placeholder={t('evv:selectUsers')}
                formatOptionLabel={formatOptionLabel}
                getOptionLabel={(option: any) => `${option.firstName} ${option.lastName}`}
                getOptionValue={(option: any) => option.userId}
                styles={customPaddingStyles}
                isLoading={fetchEmployeesStatus === 'loading'}
                isDisabled={
                  (unlinkEvvEmployeeStatus === 'loading' && currentMappedEmployee === data.row.original.id) ||
                  (mapEvvEmployeeStatus === 'loading' && currentMappedEmployee === data.row.original.id)
                }
              />
            </Box>
          );
        },
      },
      {
        Header: '',
        accessor: 'first',
        width: 265,
        Cell: (data: CellValue) => {
          return (
            <Flex>
              <Box mr="2">
                {data.row.original.user_id ? (
                  <Button
                    _hover={{ bg: 'secondary.100' }}
                    fontWeight="bold"
                    background="rgba(250, 85, 85, 0.2)"
                    color="rgba(250, 85, 85, 1)"
                    isLoading={unlinkEvvEmployeeStatus === 'loading' && currentMappedEmployee === data.row.original.id}
                    onClick={() => onUnlinkEvvEmployee(data.row.original.id)}
                  >
                    {t('evv:removeMapping')}
                  </Button>
                ) : (
                  <Button
                    _hover={{ bg: 'rgba(255, 103, 32, 0.2)' }}
                    background="rgba(255, 103, 32, 0.1)"
                    colorScheme="rgba(255, 103, 32, 0.3)"
                    color="secondary.500"
                    isLoading={mapEvvEmployeeStatus === 'loading' && currentMappedEmployee === data.row.original.id}
                    onClick={() => onMapEvvEmployee(data.row.original.id)}
                  >
                    {t('evv:mapEmployee')}
                  </Button>
                )}
              </Box>
              <Tooltip label={t('evv:updateUser')} hasArrow bg="black">
                <IconButton
                  aria-label="Edit"
                  bg="rgba(38, 103, 201, 0.1)"
                  icon={<Icon as={IconPen} />}
                  size="sm"
                  m="1"
                  _hover={{ bg: 'rgba(38, 103, 201, 0.2)' }}
                  onClick={() => navigate(routePaths.evv.editEmployee.replace(':id', data.row.original?.id))}
                />
              </Tooltip>
              <Tooltip label={t('evv:deleteUser')} hasArrow bg="black">
                <IconButton
                  aria-label="Delete"
                  bg="rgba(255, 83, 83, 0.1)"
                  icon={<Icon as={IconTrashbin} />}
                  size="sm"
                  m="1"
                  _hover={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                  onClick={() => handleEmployeeDelete(data.row.original.id)}
                />
              </Tooltip>
            </Flex>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentMappedEmployee, mappedUsers, mapEvvEmployeeStatus, unlinkEvvEmployeeStatus, fetchEmployeesStatus]
  );

  const formatOptionLabel = (option: any) => {
    return (
      <Box>
        <Text fontSize="sm" fontWeight={600}>
          {option.firstName + ' ' + option.lastName}
        </Text>
        <Text fontSize="sm" fontWeight={400}>
          {option.email}
        </Text>
      </Box>
    );
  };

  const onMapEvvEmployee = (evvEmployeeId: string) => {
    if (mappedUsers && mappedUsers[evvEmployeeId] && mappedUsers[evvEmployeeId].hasOwnProperty('user')) {
      const postData: IMapEvvEmployeeToTimeeroUser = {
        employeeId: evvEmployeeId,
        userId: mappedUsers[evvEmployeeId].user.userId,
      };
      setCurrentMappedEmployee(evvEmployeeId);
      mutateAsyncMapEvvEmployee(postData).then(() => {
        setCurrentMappedEmployee(null);
      });
    }
  };

  const onUnlinkEvvEmployee = (evvEmployeeId: string) => {
    setCurrentMappedEmployee(evvEmployeeId);
    const postData: IUnlinkEvvEmployeeToTimeeroUser = {
      employeeId: evvEmployeeId,
    };

    mutateAsyncUnlinkEvvEmployee(postData).then(() => {
      setCurrentMappedEmployee(null);
      setMappedUsers((prevValue: any) => ({
        ...prevValue,
        [evvEmployeeId]: null,
      }));
    });
  };

  // delete employee handler
  const handleEmployeeDelete = (id: string) => {
    setDeleteId(id);
    onDeleteModalOpen();
  };

  const deleteEmployee = (_e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    mutateAsyncDeleteEmployee(deleteId as string).then(() => {
      onDeleteModalClose();
      setDeleteId(null);
    });
  };

  const handlePageSizeChange = (pageSize: number) => {
    setSearchQuery(queries => ({ ...queries, page_size: pageSize }));
  };

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: IEvvEmployeesSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchInputChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase().trim();
      setSearchQuery({ ...searchQuery, page: 1, search: searchValue });
    }, 400),
    [searchQuery]
  );

  return (
    <div>
      <Flex justifyContent="space-between">
        <Title hideBackButton={true}>{t('evv:evvEmployees')}</Title>
        <Button colorScheme="secondary" onClick={() => navigate(routePaths.evv.addEmployee)}>
          {t('evv:addEmployee')}
        </Button>
      </Flex>
      <Flex justifyContent="flex-end" mt="4">
        <SearchField
          inputGroupProps={{ maxW: '350px', width: '100%' }}
          inputProps={{
            placeholder: t('evv:searchEmployee'),
            onChange: handleSearchInputChange,
          }}
        />
      </Flex>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={onDeleteModalClose}
        title={t('evv:deleteEvvEmployeeModalTitle')}
        subTitle={t('evv:deleteEvvEmployeeModalSubtitle')}
        confirmButtonLabel={t('common:delete')}
        confirmButtonProps={{
          bg: 'rgba(255, 83, 83, 0.1)',
          color: 'secondary.500',
          _hover: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          isLoading: deleteEmployeeStatus === 'loading',
          onClick: deleteEmployee,
        }}
      />

      <Table
        columns={columns}
        data={data?.payload || []}
        paginationStyle="rowsPerPage"
        paginationData={data?.meta_data?.pagination}
        onPageChange={handlePageChange}
        handlePageSizeChange={handlePageSizeChange}
        isLoading={status === 'loading'}
        enableRowSelect
      />
    </div>
  );
};

export default EmployeesList;
